import React, { useEffect, useRef, useState } from 'react';
import { Keyboard, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

/**
 * Height (in px) the on-screen keyboard currently occludes at the bottom of the
 * viewport, or 0 when hidden.
 *
 * None of our target platforms shrink the RN layout on their own — iOS never
 * does, Android is configured `softwareKeyboardLayoutMode: 'pan'` (app.config.js),
 * and web needs `visualViewport` — so the conversation surface must make room
 * for the keyboard itself. Measuring the exact height (rather than a fixed
 * offset) lets the chat area contract by precisely the keyboard's size.
 */
function useKeyboardHeight(): number {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const vv =
        typeof window !== 'undefined' ? window.visualViewport ?? null : null;
      if (!vv) return;
      const onResize = () => {
        // Portion of the layout viewport hidden by the keyboard: the gap between
        // the layout viewport bottom and the (shrunk, possibly offset) visual
        // viewport bottom.
        const occluded = Math.max(
          0,
          window.innerHeight - vv.height - vv.offsetTop,
        );
        // Ignore sub-pixel / browser-toolbar jitter so we don't thrash on scroll.
        setHeight(occluded > 80 ? occluded : 0);
      };
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
      onResize();
      return () => {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
      };
    }

    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const showSub = Keyboard.addListener(showEvent, (e) =>
      setHeight(e.endCoordinates?.height ?? 0),
    );
    const hideSub = Keyboard.addListener(hideEvent, () => setHeight(0));
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  return height;
}

/**
 * Scrolling surface of the registration chat: auto-scrolls to the newest
 * bubble, keeps the composer (input + CTA) pinned under the conversation, and
 * shrinks the conversation area by exactly the keyboard height when it opens so
 * the composer sits directly above the keyboard instead of leaving a dead gap.
 */
export const ChatConversation: React.FC<{
  children: React.ReactNode;
  composer?: React.ReactNode;
  testID?: string;
}> = ({ children, composer, testID }) => {
  const scrollRef = useRef<ScrollView>(null);
  const { bottom } = useSafeAreaInsets();
  const keyboardHeight = useKeyboardHeight();
  const keyboardOpen = keyboardHeight > 0;

  // The keyboard overlaps the home-indicator safe area, so only reserve that
  // inset while the keyboard is hidden.
  const spacerHeight = keyboardOpen ? Math.max(0, keyboardHeight - bottom) : 0;

  useEffect(() => {
    // Keep the latest bubble and the composer in view across a keyboard toggle.
    if (keyboardOpen) {
      requestAnimationFrame(() =>
        scrollRef.current?.scrollToEnd({ animated: true }),
      );
    }
  }, [keyboardOpen]);

  return (
    <View style={styles.flex}>
      <ScrollView
        ref={scrollRef}
        style={styles.flex}
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
        onContentSizeChange={() => scrollRef.current?.scrollToEnd({ animated: true })}
        testID={testID ?? 'chat-conversation'}
      >
        {children}
      </ScrollView>
      {composer ? (
        <View style={[styles.composer, { paddingBottom: keyboardOpen ? 8 : 16 + bottom }]}>
          {composer}
        </View>
      ) : null}
      {/* Reserves the keyboard's footprint, contracting the ScrollView above. */}
      <View style={{ height: spacerHeight }} />
    </View>
  );
};

const styles = StyleSheet.create({
  flex: { flex: 1 },
  content: {
    padding: 16,
    paddingBottom: 8,
    width: '100%',
    maxWidth: 560,
    alignSelf: 'center',
  },
  composer: {
    paddingHorizontal: 16,
    paddingTop: 8,
    width: '100%',
    maxWidth: 560,
    alignSelf: 'center',
  },
});

export default ChatConversation;
